// Migration of a HOSTED database to a new design version (task O2b; backend:
// routers/migrations.py, operate/migration.py). A project manager freezes the current design
// (POST .../dbdesign/freeze), reads the plan (GET .../versions/{n}/migration-plan: every
// operation safe / drop / refused, the live rows that would break a new key link or rule),
// ticks the drops to apply and retypes each one's name, may run it on a copy first
// (POST .../dry-run) and applies it (POST .../migrate, after retyping the database name).
// Both answer 202; this panel polls GET .../versions/{n} until the job is over. An unticked
// drop is skipped; the server checks the ticks and the names again (422). A removed shared
// entity is a drop of its hub table. An accepted drop is {stable_id, name}.
import { useCallback, useEffect, useRef, useState } from "react";
import { api } from "../../api";
import { Badge, Btn, ConfirmDialog, Spinner } from "../../components/Shared";
import { ErrorNote } from "../../components/Feedback";
import { buildPolling } from "./BuildPanel";

// convert (task 2026-10-21): a column built as timestamp becomes timestamptz, its stored times
// read in the zone the version was frozen with (o.zone); op change_zone (review fixes): the
// database moves to the zone the new version was frozen with (o.old_zone -> o.zone). Both
// listed apart and named in the confirm.
const OP_COLOR = { safe: "green", convert: "blue", drop: "amber", refused: "red" };
const box = { marginTop: 14, padding: "12px 16px", borderRadius: 8, border: "1px solid #e5e7eb", fontSize: 13 };
const mono = { fontFamily: "'JetBrains Mono', monospace" };

// The version a migration would go to: the newest one above the hosted version that is not
// migrated yet (frozen, failed or migrating). A split or a merge (migration.restructure) is not
// one: RestructurePanel follows it, and a failed one is planned again there.
export function migrationTarget(versions, hosting) {
  if (!hosting) return null;
  return versions.find(v => v.version > hosting.version && ["frozen", "failed", "migrating"].includes(v.status)
                            && !(v.migration && v.migration.restructure)) || null;
}

function dropLabel(o) {
  if (o.op === "drop_entity") return `Drop shared-entity table ${o.table}`;
  return `Drop ${o.column ? `column ${o.table}.${o.column}` : `table ${o.table}`}`;
}

function DryRun({ dry }) {
  if (!dry) return null;
  if (dry.status === "running") return <div style={box} role="status"><Spinner /> Dry run on a copy of the database…</div>;
  const ok = dry.status === "ok";
  return (
    <div data-testid="dry-run" style={{ ...box, background: ok ? "rgba(0,150,64,0.06)" : "rgba(220,38,38,0.06)" }}>
      <strong>Dry run on a copy: {ok ? "it would succeed" : "it would fail"}</strong>
      {ok && <> — {dry.statements} statement(s) in {dry.seconds} s.</>}
      {!ok && <div style={{ marginTop: 4 }}>{dry.error}{dry.detail ? ` (${dry.detail})` : ""}</div>}
      {dry.row_counts && (
        <div style={{ marginTop: 4, color: "#374151" }}>
          Rows afterwards: {Object.entries(dry.row_counts).map(([t, n]) => `${t} ${n}`).join(" · ")}
        </div>
      )}
      {(dry.skipped || []).length > 0 && <div style={{ marginTop: 4 }}>Skipped: {dry.skipped.join(" ")}</div>}
      {(dry.portal_changes || []).length > 0 && (
        <div style={{ marginTop: 4 }}>Portal tables: {dry.portal_changes.join(" ")}</div>
      )}
    </div>
  );
}

export default function MigrationPanel({ ws, versions, canManage, onChange }) {
  const hosting = ws.hosting;
  const projectId = ws.project_id;
  const target = migrationTarget(versions, hosting);
  const [polled, setPolled] = useState(null);
  const current = polled && target && polled.version === target.version ? polled : target;
  const n = current ? current.version : null;
  const dryRunning = ((current && current.migration) || {}).dry_run?.status === "running";
  const running = !!current && (current.status === "migrating" || dryRunning);
  const [plan, setPlan] = useState(null);
  const [ticked, setTicked] = useState({});        // stable_id -> retyped name
  const [busy, setBusy] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [error, setError] = useState(null);
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  const loadPlan = useCallback(async () => {
    if (n == null) { setPlan(null); return; }
    try {
      setPlan(await api.get(`/projects/${projectId}/dbdesign/versions/${n}/migration-plan`));
    } catch (e) {
      setPlan(null);
      setError(e);
    }
  }, [projectId, n]);

  useEffect(() => { if (!running) loadPlan(); }, [loadPlan, running]);

  // Follow a migration or a dry run until it is over.
  useEffect(() => {
    if (!running) return undefined;
    let live = true;
    let timer;
    const tick = async () => {
      try {
        const v = await api.get(`/projects/${projectId}/dbdesign/versions/${n}`);
        if (!live) return;
        setPolled(v);
        const still = v.status === "migrating" || (v.migration || {}).dry_run?.status === "running";
        if (still) timer = setTimeout(tick, buildPolling.intervalMs);
        else onChangeRef.current();
      } catch (e) {
        if (!live) return;
        setError(e);
        timer = setTimeout(tick, buildPolling.intervalMs);
      }
    };
    timer = setTimeout(tick, buildPolling.intervalMs);
    return () => { live = false; clearTimeout(timer); };
  }, [running, projectId, n]);

  if (!hosting) return null;

  const call = async (fn) => {
    setBusy(true);
    setError(null);
    try {
      await fn();
    } catch (e) {
      setError(e);
    } finally {
      setBusy(false);
    }
  };
  const freeze = () => call(async () => {
    await api.post(`/projects/${projectId}/dbdesign/freeze`);
    setTicked({});
    onChange();
  });
  const drops = (plan && plan.drops) || [];
  const acceptDrops = drops.filter(d => d.stable_id in ticked)
    .map(d => ({ stable_id: d.stable_id, name: ticked[d.stable_id] }));
  const ready = drops.every(d => !(d.stable_id in ticked) || ticked[d.stable_id] === d.name);
  const start = (what) => call(async () => {
    setConfirming(false);
    const v = await api.post(`/projects/${projectId}/dbdesign/versions/${n}/${what}`, { accept_drops: acceptDrops });
    setPolled({ ...v, migration: what === "dry-run" ? { ...(v.migration || {}), dry_run: { status: "running" } } : v.migration });
    onChange();
  });
  const lastDone = versions.find(v => v.status === "migrated" && v.version === hosting.version);
  const converts = ((plan && plan.operations) || []).filter(o => o.status === "convert" && o.op === "change_type");
  const zoneChange = ((plan && plan.operations) || []).find(o => o.op === "change_zone" && o.status === "convert");

  return (
    <section aria-label="Migration" data-testid="migration-panel" style={{ marginTop: 20, paddingTop: 16, borderTop: "1px solid #e5e7eb" }}>
      <div style={{ display: "flex", justifyContent: "space-between", gap: 12, flexWrap: "wrap", alignItems: "center" }}>
        <h3 style={{ fontSize: 15, margin: 0 }}>Migrate the hosted database</h3>
        {canManage && !running && (
          <Btn variant="secondary" onClick={freeze} disabled={busy}>
            {target ? "Freeze the design again" : "Freeze the design for a migration"}
          </Btn>
        )}
      </div>
      <p style={{ margin: "6px 0 0", fontSize: 13, color: "#374151" }}>
        {ws.database_name} holds version {hosting.version}{hosting.mode === "maintenance" ? " and is in maintenance (a migration is running)" : ""}.
        A migration changes it in place in one transaction: every row stays, new columns start empty
        (nothing is reloaded from the files), and a dump is written to S3 first.
      </p>
      <ErrorNote error={error} />
      {lastDone && lastDone.has_migration_sql && (
        <div style={{ marginTop: 8, fontSize: 13 }}>
          <button type="button" style={{ background: "none", border: "none", padding: 0, cursor: "pointer", color: "var(--dp-accent-dark)", fontFamily: "inherit", fontSize: 13 }}
                  onClick={() => api.download(`/projects/${projectId}/dbdesign/versions/${lastDone.version}/migration.sql`,
                                              `${ws.database_name}_v${lastDone.version}_migration.sql`).catch(setError)}>
            migration.sql of version {lastDone.version}
          </button>
        </div>
      )}
      {current && current.status === "migrating" && (
        <div role="status" style={box}><Spinner /> Migrating {ws.database_name} to version {n}… data entry waits until it ends.</div>
      )}
      {current && current.status === "failed" && current.migration && current.migration.from_version != null && (
        <div role="alert" style={{ ...box, background: "rgba(220,38,38,0.06)", color: "#991b1b" }}>
          The migration to version {n} failed; {ws.database_name} is unchanged. See the build log above.
        </div>
      )}
      {current && <DryRun dry={(current.migration || {}).dry_run} />}
      {plan && !running && (
        <div data-testid="migration-plan" style={{ marginTop: 12 }}>
          <div style={{ fontSize: 13, color: "#374151" }}>
            Plan from version {plan.from_version} to {plan.to_version}: {plan.summary.safe} safe ·{" "}
            {plan.summary.convert ? `${plan.summary.convert} time zone change(s) · ` : ""}
            {plan.summary.drop} drop(s) · {plan.summary.refused} refused
          </div>
          {plan.operations.length === 0 && <p style={{ fontSize: 13 }}>Nothing changes.</p>}
          <ul aria-label="Planned changes" style={{ listStyle: "none", margin: "8px 0 0", padding: 0 }}>
            {plan.operations.map((o, i) => (
              <li key={`${o.stable_id}-${o.op}-${i}`} style={{ padding: "6px 0", borderTop: "1px solid #f3f4f6", fontSize: 13 }}>
                <Badge color={OP_COLOR[o.status] || "gray"}>{o.status}</Badge>{" "}
                <span>{o.summary}</span>
                {o.reason && <div style={{ color: "#991b1b", marginTop: 2 }}>{o.reason}</div>}
                {o.warning && <div style={{ color: "#92400e", marginTop: 2 }}>{o.warning}</div>}
                {o.note && <div style={{ color: "#374151", marginTop: 2 }}>{o.note}</div>}
                {o.checked && <div style={{ color: "#374151", marginTop: 2 }}>Rows: {o.checked}.</div>}
                {(o.issues || []).slice(0, 20).map((x, j) => (
                  <div key={j} style={{ color: "#991b1b", marginTop: 2 }}>{x.message}</div>
                ))}
                {o.status === "drop" && canManage && (
                  <div style={{ marginTop: 4, display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                    <label>
                      <input type="checkbox" checked={o.stable_id in ticked}
                             onChange={e => setTicked(t => {
                               const next = { ...t };
                               if (e.target.checked) next[o.stable_id] = ""; else delete next[o.stable_id];
                               return next;
                             })} />{" "}
                      {dropLabel(o)}
                    </label>
                    {o.stable_id in ticked && (
                      <label>
                        Retype <strong style={mono}>{o.column || o.table}</strong>{" "}
                        <input aria-label={`Retype ${o.column || o.table} to drop it`}
                               value={ticked[o.stable_id]}
                               onChange={e => setTicked(t => ({ ...t, [o.stable_id]: e.target.value }))} />
                      </label>
                    )}
                  </div>
                )}
              </li>
            ))}
          </ul>
          {plan.blocking.length > 0 && (
            <div role="alert" style={{ ...box, background: "rgba(220,38,38,0.06)", color: "#991b1b" }}>
              <strong>This plan cannot be applied:</strong>
              <ul style={{ margin: "6px 0 0", paddingLeft: 18 }}>{plan.blocking.map((b, i) => <li key={i}>{b}</li>)}</ul>
            </div>
          )}
          {canManage && plan.can_apply && (
            <div style={{ display: "flex", gap: 8, marginTop: 12, flexWrap: "wrap" }}>
              <Btn variant="secondary" disabled={busy || !ready} onClick={() => start("dry-run")}>Dry run on a copy</Btn>
              <Btn disabled={busy || !ready} onClick={() => setConfirming(true)}>Apply migration</Btn>
            </div>
          )}
        </div>
      )}
      {confirming && plan && (
        <ConfirmDialog title={`Migrate ${ws.database_name} to version ${n}?`} confirmWord={ws.database_name}
                       confirmLabel="Migrate" onConfirm={() => start("migrate")} onClose={() => setConfirming(false)}>
          <p style={{ margin: 0, fontSize: 14, color: "#374151" }}>
            {plan.summary.safe} change(s) run in one transaction; the tables they alter are locked while it
            runs and data entry answers "maintenance". A dump is written to S3 first.
            {acceptDrops.length > 0
              ? ` Dropped for good: ${acceptDrops.map(d => d.name).join(", ")}.`
              : drops.length > 0 ? " No drop is ticked: they are skipped." : ""}
            {converts.length > 0 &&
              ` Converted to timestamptz, every stored time read as a local time in ${converts[0].zone}: ${converts.map(o => `${o.table}.${o.column}`).join(", ")}.`}
            {zoneChange &&
              ` The time zone of ${ws.database_name} changes from ${zoneChange.old_zone} to ${zoneChange.zone}.`}
          </p>
        </ConfirmDialog>
      )}
    </section>
  );
}
